import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../../../theming/useTheme';
import { TextLinkComponent, ThemedLinkStyle, generateLinkStyleFromTheme } from '../../../../partials/link/TextLink';
import { HeaderLinksDataProps } from '../Header';
import { notActiveLinkIdleColor } from '../header.utils';
import { getGeneratedGlobalStyles } from '../../../../../typography/globalTypographyStyles';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { VrLineHeight } from '../../../../../theming/verticalRhythm/verticalRhythm';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';

const componentConfigThemeKey = 'primaryLinks';

export interface PrimaryLinksThemeProps {
  activeBorderBottom: string
  hoverOtherLinksColors: string
  vrLineHeight: VrLineHeight
  idleColor: string,
  activeStateColor: string,
  hoverStateColor: string,
}

interface state {
  isHoveredLink: boolean
}

const radiusBorder = 4;

const getLinkStyle = (activeBorderBottom: string, isActive: boolean = false) => {
  return StyleSheet.create({
    link: {
      height: '100%',
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      ':before': isActive ? {
        height: `${radiusBorder}px`,
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        content: "''",
        backgroundColor: activeBorderBottom,
      } : {},
    },
  });
};

const getContainerStyle = (vrLineHeight: VrLineHeight) => {
  return StyleSheet.create({
    linksContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      },
    },
  });
};

export interface PrimaryLinksDataProps {
  links: HeaderLinksDataProps[]
  pagePath: string
}

export type PrimaryLinksComponentProps = PrimaryLinksDataProps & PrimaryLinksThemeProps;

const additionalStyle = {
  [MEDIA_BREAKPOINTS.X_LARGE]: {
    display: 'block',
  },
  [MEDIA_BREAKPOINTS.LARGE]: {
    display: 'block',
  },
  [MEDIA_BREAKPOINTS.MEDIUM]: {
    display: 'none',
  },
  [MEDIA_BREAKPOINTS.SMALL]: {
    display: 'none',
  },
};

const createLink = ({ idleColor, activeStateColor, hoverStateColor, activeBorderBottom, hoverOtherLinksColors, vrLineHeight }: ThemedLinkStyle & PrimaryLinksThemeProps, isHoveredLink: boolean, pagePath: string) => (
  (linkData: HeaderLinksDataProps, index: number) => {
    const isActiveLink = linkData.href === pagePath;
    const style = getLinkStyle(activeBorderBottom, isActiveLink);
    const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
    const linkStyle = generateLinkStyleFromTheme({ idleColor: calculatedIdleColor, activeStateColor, hoverStateColor }, additionalStyle);
    const contentStyle = StyleSheet.create(isActiveLink ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());
    return (
      <TextLinkComponent key={index} href={linkData.href} linkStyle={linkStyle}>
        <span className={css(contentStyle.style, style.link)}>{linkData.text}</span>
      </TextLinkComponent>
    );
  }
);

export class PrimaryLinksComponent extends React.Component<PrimaryLinksComponentProps, state> {
  constructor(props: PrimaryLinksComponentProps) {
    super(props);
    this.state = {
      isHoveredLink: false,
    };
  }

  onLinkHover = () => {
    this.setState({
      isHoveredLink: true,
    });
  };

  onLinkLeave = () => {
    this.setState({
      isHoveredLink: false,
    });
  };

  render() {
    const { links, vrLineHeight, pagePath } = this.props;
    const { isHoveredLink } = this.state;
    const style = getContainerStyle(vrLineHeight);
    return (
      <div className={css(style.linksContainer)} onMouseEnter={this.onLinkHover} onMouseLeave={this.onLinkLeave}>
        {links.map(createLink(this.props, isHoveredLink, pagePath))}
      </div>
    );
  }
}


const mapThemeProps = (theme: PrimaryLinksThemeProps) => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    activeBorderBottom: theme.activeBorderBottom,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
    vrLineHeight: theme.vrLineHeight,
  };
};

export const PrimaryLinks = (props: PrimaryLinksDataProps) => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps);
  return <ErrorBoundary><PrimaryLinksComponent {...themeContext} {...props} /></ErrorBoundary>;
};
